'use client'
import { useEffect, useState } from 'react'
import { Media } from '@/payload-types'
import { Carousel, FlowbiteCarouselTheme } from 'flowbite-react'
import ImageComponent from '@/components/media/imageComponent'
import { ArrowRight, ArrowLeft } from 'lucide-react'
import Image from 'next/image'
import useTouchDetect from '@/utils/react-hooks/useTouchDetect'
import {
  EmbeddedVideoItem,
  getYoutubeSource,
  getVimeoSource,
  getFacebookSource,
  getYoutubeThumbnail,
  getVimeoThumbnail,
  getFacebookThumbnail,
} from '../light-gallery/helperFunctionsLightGallery'
import { VideoStatus } from '@/enum/enum'
import { getVideoSource, VideoSource } from '@/components/media/getVideoSource'

interface IProps {
  gallery: (string | Media)[] | null
  externalVideos: EmbeddedVideoItem[] | null
  onOpen: (index: number) => void
}

const payload_url = process.env.NEXT_PUBLIC_PAYLOAD_URL ? process.env.NEXT_PUBLIC_PAYLOAD_URL : ''

export const getVideoCoverImageSource = (media: Media) => {
  if (media.mimeType === 'externalVideo') {
    return media.coverImage as string
  }

  const videoSource = getVideoSource(media)
  if (videoSource == VideoSource.MUX) {
    if (media.mux?.status === VideoStatus.PENDING) {
      return '/images/video-in-progress.png'
    } else if (media.mux?.status === VideoStatus.FAILED) {
      return '/images/video-failed.png'
    } else {
      return `${payload_url}${media.coverImage && typeof media?.coverImage === 'object' ? media.coverImage.sizes?.medium?.url : ''}`
    }
  } else {
    return `${payload_url}${media.coverImage && typeof media?.coverImage === 'object' ? media.coverImage.sizes?.medium?.url : ''}`
  }
}

export const CarouselGalleryComponent = ({ gallery, externalVideos, onOpen }: IProps) => {
  const [viewport, setViewport] = useState('desktop')
  const [hidden, setHidden] = useState('hidden')
  const [index, setIndex] = useState(0)
  const isTap = useTouchDetect()

  useEffect(() => {
    window.innerWidth < 768 ? setViewport('mobile') : setViewport('desktop')
    window.addEventListener('resize', () => {
      window.innerWidth < 768 ? setViewport('mobile') : setViewport('desktop')
    })
    setHidden('')
  }, [viewport])
  
  const externalVideosLikeMedia: Media[] | undefined = externalVideos?.map(
    (video: EmbeddedVideoItem) => {
      const media: Media = {
        id: video.id ?? '',
        title: video.title,
        createdAt: '2025-09-21T00:00:00Z',
        updatedAt: '2025-09-21T00:00:00Z',
        mimeType: 'externalVideo',
      }
      if (video.source === 'youtube') {
        media.url = getYoutubeSource(video.videoId)
        media.coverImage = getYoutubeThumbnail(video.videoId)
      } else if (video.source === 'vimeo') {
        media.url = getVimeoSource(video.videoId)
        media.coverImage = getVimeoThumbnail(video.videoId)
      } else if (video.source === 'facebook') {
        media.url = getFacebookSource(video.videoId)
        media.coverImage = getFacebookThumbnail(video.faceThumb, 'medium')
      }
      return media
    },
  )

  gallery = [...(gallery ?? []), ...(externalVideosLikeMedia ?? [])]

  const NUM_IMAGES = gallery ? gallery.length : 0
  const IMAGES_PER_CARD = viewport === 'mobile' ? 4 : 6
  const NUM_CARDS = Math.ceil(NUM_IMAGES / IMAGES_PER_CARD)
  const image_list:
    | {
        media?: string | Media | null
        id?: string | null
      }[][] = []
  let curr = 0
  for (let i = 0; i < NUM_CARDS; i++) {
    image_list[i] = []
    for (let j = 0; j < IMAGES_PER_CARD; j++) {
      if (gallery && curr < gallery.length) {
        image_list[i].push({ media: gallery[curr] as Media })
      }
      curr++
    }
  }

  const customTheme: FlowbiteCarouselTheme = {
    root: {
      base: 'relative h-full w-full',
      leftControl:
        'font-bold absolute bottom-7 sm:bottom-5 right-1/2 flex items-center justify-center focus:outline-none mr-[45px] sm:mr-[55px]',
      rightControl:
        'font-bold absolute bottom-7 sm:bottom-5 flex left-1/2 items-center justify-center focus:outline-none ml-[45px] sm:ml-[55px]',
    },
    indicators: {
      active: {
        off: 'bg-white/50 hover:bg-white',
        on: 'bg-white dark:bg-gray-800',
      },
      base: 'hidden h-3 w-3 rounded-full',
      wrapper: 'absolute bottom-5 left-1/2 flex -translate-x-1/2 space-x-3',
    },
    item: {
      base: `absolute left-1/2 top-1/2 block w-full -translate-x-1/2 -translate-y-1/2 ${
        NUM_CARDS < 2 ? 'pb-4' : 'pb-16'
      }`,
      wrapper: {
        off: 'w-full flex-shrink-0 transform cursor-default snap-center',
        on: 'w-full flex-shrink-0 transform snap-center',
      },
    },
    control: {
      base: 'relative inline-flex justify-center items-center rounded-full group-focus:outline-none sm:h-10 sm:w-10',
      icon: 'text-black h-6 w-6 font-extrabold !stroke-[3px]',
    },
    scrollContainer: {
      base: 'flex h-full snap-mandatory overflow-y-hidden overflow-x-auto scroll-smooth rounded-lg',
      snap: 'snap-x',
    },
  }

  let mobileHeight = ''
  let desktopHeight = ''

  if (NUM_CARDS < 2) {
    if (NUM_IMAGES < 3) {
      mobileHeight = 'h-[167px]'
    } else {
      mobileHeight = 'h-[286px]'
    }
    if (NUM_IMAGES < 4) {
      desktopHeight = 'md:h-[185px] lg:h-[240px]'
    } else {
      desktopHeight = 'md:h-[322px] lg:h-[370px]'
    }
  } else {
    mobileHeight = 'h-[334px]'
    desktopHeight = 'md:h-[370px] lg:h-[479px]'
  }

  const handleClickOrTap = (e: React.MouseEvent | React.TouchEvent, realImageIndex: number) => {
    e.preventDefault()
    e.stopPropagation()
    onOpen(realImageIndex)
  }

  return (
    <>
      {NUM_IMAGES > 0 && (
        <div className={`flex flex-col mt-[37px] ${mobileHeight} ${desktopHeight} items-center`}>
          <div className={`${hidden} h-full w-full`}>
            <Carousel
              theme={customTheme}
              id="carousel-gallery"
              slide={false}
              indicators={true}
              onSlideChange={(slide: number) => setIndex(slide)}
              rightControl={
                <ArrowRight
                  className={`${
                    NUM_CARDS < 2 ? 'hidden' : ''
                  } stroke-[3px] text-gray-600 h-[20px] w-[20px]"`}
                />
              }
              leftControl={
                <ArrowLeft
                  className={`${
                    NUM_CARDS < 2 ? 'hidden' : ''
                  } stroke-[3px] text-gray-600 h-[20px] w-[20px]"`}
                />
              }
            >
              {image_list.map((images, cardIndex) => (
                <div
                  key={cardIndex}
                  className={`h-full w-full grid ${
                    NUM_IMAGES < 3 ? '!grid-rows-1' : '!grid-rows-2'
                  } ${
                    NUM_IMAGES < 4 ? 'md:!grid-rows-1' : 'md:!grid-rows-2'
                  } grid-cols-2 md:grid-cols-3 justify-between gap-2.5 lg:gap-5`}
                >
                  {images.map((image, index) => {
                    const realImageIndex = cardIndex * IMAGES_PER_CARD + index
                    return (
                      typeof image?.media === 'object' &&
                      image?.media?.url && (
                        <button
                          key={index}
                          onClick={
                            viewport === 'mobile'
                              ? undefined
                              : (e) => handleClickOrTap(e, realImageIndex)
                          }
                          onTouchEnd={
                            viewport === 'mobile' && isTap
                              ? (e) => handleClickOrTap(e, realImageIndex)
                              : undefined
                          }
                        >
                          {image.media.mimeType?.startsWith('image') ? (
                            <ImageComponent
                              src={`${payload_url}${image?.media?.sizes?.medium?.url}`}
                              alt={`${image?.media?.title}_#${realImageIndex + 1}`}
                            />
                          ) : (
                            <div className="h-full flex justify-center items-center">
                              <ImageComponent
                                src={getVideoCoverImageSource(image.media) ?? ''}
                                alt={`${image?.media?.title}_#${realImageIndex + 1}`}
                              />
                              <div className="absolute">
                                <div className="abosolute inset-0 flex items-center justify-center">
                                  <div className="absolute flex items-center justify-center">
                                    <div className="rounded-full w-20 h-20 border-[3px] border-[#C13D20] bg-black bg-opacity-70"></div>
                                    <Image
                                      className="absolute left-6"
                                      src={'/images/favicons/play icon.svg'}
                                      alt={'Button Image'}
                                      width={40}
                                      height={40}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </button>
                      )
                    )
                  })}
                </div>
              ))}
            </Carousel>
          </div>
          <div
            className={`${
              NUM_CARDS < 2 ? 'hidden' : ''
            } relative -top-[39px] sm:-top-[30px] flex justify-center items-center z-[0]`}
          >
            <div
              className={`${hidden} absolute flex h-[29px] sm:h-[40px] w-[60px] sm:w-[94px] border-2 border-gray-200 shadow-cards rounded-lg items-center justify-center text-gray-600 font-medium text-xs sm:text-base`}
            >
              {index + 1} of {NUM_CARDS}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CarouselGalleryComponent
